import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useDAList } from '../hooks/useApiQueries';
import { DAItem } from '../hooks/useApiQueries';
import { useAuthRedirect } from '../hooks/useAuthRedirect';
import useSearchHandler from '../hooks/useSearchHandler';
import theme from '../theme';
import { formatDaFeeTtc, formatDateToDDMMYYYY, getDaFeeTtc } from '../utils';
import { useAuth } from './AuthContext';
import PaymentModal from './CGList/PaymentModal';
import CommentsManager from './Comments/CommentsManager';
import AgencyDAList from './DAList/AgencyDAList';
import SubmitFormDA from './DealsList/DealStageActions/SubmitFormDA';

// Interfaces pour les données d'organisation et de personne
interface OrganizationData {
  id: number;
  name: string;
  address: string;
  town: string;
  post_code: string;
  email?: string;
  person_email?: string;
  [key: string]: any;
}

interface PersonData {
  id: number;
  name: string;
  email?: string;
  secondary_email?: string;
  [key: string]: any;
}

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const SortIcon = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: theme.spacing(1),
  color: 'white',
}));

const mapSortColumnToQuery = (column: string): string => {
  const columnMap: { [key: string]: string } = {
    'R.id': 'R.id',
    'R.createdAt': 'R.created_at',
    'R.updatedAt': 'R.updated_at',
    'R.status': 'R.status',
    'R.regPlate': 'R.reg_plate',
    'R.purchaseDateTime': 'R.purchase_date_time',
  };
  return columnMap[column] || 'R.id';
};

const DELETED_TAB_INDEX = 4;

const DAList = () => {
  const { currentUser } = useAuth();
  const { redirectToLogin } = useAuthRedirect();
  const { t } = useTranslation('common');
  const userRole = currentUser?.data?.data?.role;
  const restrictedUserId = 'EJBJf06e4WarfF1ruTt3bb6uHE42'; // François peut regénérer les factures
  const userPipedrive = currentUser?.data?.data?.idPipedrive ?? 188;
  const orgIdValue = userRole === 'agency' ? userPipedrive : currentUser?.data?.organization?.value;

  // Si l'utilisateur est une agence, afficher la vue agence avec les tableaux colorés
  if (userRole === 'agency') {
    return (
      <Container
        maxWidth={false}
        sx={{ padding: theme.spacing(4), backgroundColor: theme.palette.background.default, paddingTop: 0 }}
      >
        <Grid
          container
          spacing={4}
          sx={{
            marginTop: theme.spacing(0),
            width: '100%',
            maxWidth: 1300,
            mx: 'auto',
            marginBottom: theme.spacing(0),
          }}
        >
          <Grid item xs={12}>
            <AgencyDAList orgId={orgIdValue} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  const [isLoadingInvoice, setIsLoadingInvoice] = useState<boolean>(false);
  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [isFullSyncLoading, setIsFullSyncLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(userRole === 'agency' ? 0 : 2);
  const [sortColumn, setSortColumn] = useState<string>('R.id');
  const [sortOrder, setSortOrder] = useState<string>('DESC');
  const [isDeleting, setIsDeleting] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreTargetId, setRestoreTargetId] = useState<number | null>(null);

  // États pour la régénération de facture
  const [openRegenerateDialog, setOpenRegenerateDialog] = useState<boolean>(false);
  const [personId, setPersonId] = useState<number>();
  const [agencyId, setAgencyId] = useState<number>();
  const [dealToRegenerate, setDealToRegenerate] = useState<any>(null);

  const { searchQuery, hasSearched, isAutoSearchLoading, setIsAutoSearchLoading, handleSearch, resetSearch } =
    useSearchHandler();
  const autoSearchDone = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const openExternalCommentsId = searchParams.get('openExternalComments');
  const autoSearchRegPlate = searchParams.get('autoSearch');

  const status = searchQuery
    ? undefined // Si recherche, pas de filtre de status pour chercher dans tous les onglets
    : selectedTab === 0
      ? undefined
      : selectedTab === 1
        ? 'pending'
        : selectedTab === 2
          ? 'processing'
          : selectedTab === 3
            ? 'completed'
            : undefined;

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      DA_REQUEST: 'En attente de paiement',
      DA_PROCESSING: 'En attente de traitement',
      DA_COMPLETED: 'Terminé',
    };
    return statusMap[status] || status;
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'DA_REQUEST':
        return {
          backgroundColor: '#FF6060',
          color: 'white',
          opacity: 1,
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          display: 'inline-block',
        };
      case 'DA_PROCESSING':
        return {
          backgroundColor: '#FF6060',
          color: 'white',
          opacity: 0.7,
          padding: '4px 8px',
          borderRadius: '4px',
          fontWeight: 600,
          display: 'inline-block',
        };
      case 'DA_COMPLETED':
        return {
          color: '#008000',
          fontWeight: 'bold',
          fontSize: '16px',
        };
      default:
        return {
          color: 'inherit',
          fontWeight: 'normal',
        };
    }
  };

  // Vérifier l'authentification et rediriger si nécessaire
  useEffect(() => {
    if (!currentUser) {
      redirectToLogin();
    }
  }, [currentUser, redirectToLogin]);

  const { data, isLoading, error, refetch } = useDAList(
    userPipedrive,
    userRole,
    page + 1,
    rowsPerPage,
    searchQuery,
    status,
    mapSortColumnToQuery(sortColumn),
    sortOrder,
    false,
    userRole === 'agency' ? orgIdValue : undefined,
  );

  const { data: dataHidden } = useDAList(
    userPipedrive,
    userRole,
    page + 1,
    rowsPerPage,
    searchQuery,
    status, // Utilise la même logique de status que la requête principale
    mapSortColumnToQuery(sortColumn),
    sortOrder,
    true,
    userRole === 'agency' ? orgIdValue : undefined,
  );

  // Requête pour récupérer le nombre total de DA (tous statuts confondus)
  const { data: dataTotalCheck } = useDAList(
    userPipedrive,
    userRole,
    1,
    1,
    '',
    undefined, // Pas de filtre de statut pour avoir toutes les DA
    'R.id',
    'DESC',
    false,
    userRole === 'agency' ? orgIdValue : undefined,
  );

  const daListData = selectedTab === DELETED_TAB_INDEX ? dataHidden?.data || [] : data?.data || [];
  const totalItems = selectedTab === DELETED_TAB_INDEX ? dataHidden?.total || 0 : data?.total || 0;

  // Vérification spécifique pour les agences
  if (userRole === 'agency' && !orgIdValue) {
    return (
      <Container maxWidth={false}>
        <Typography variant="h6" sx={{ mt: 4, textAlign: 'center', color: 'error.main' }}>
          Erreur de configuration : Aucun ID d'organisation trouvé pour votre compte agence. Veuillez contacter le
          support au 07 80 94 97 37.
        </Typography>
      </Container>
    );
  }

  useEffect(() => {
    if (!isLoading && daListData) {
      setIsTableLoading(false);
    }
  }, [isLoading, daListData]);

  useEffect(() => {
    if (searchQuery !== '') {
      setPage(0);
    }
  }, [searchQuery]);

  // Gérer l'ouverture automatique du drawer des commentaires externes depuis l'email
  useEffect(() => {
    if (openExternalCommentsId && !isLoading) {
      const targetId = parseInt(openExternalCommentsId);

      // Fonction pour rechercher l'ID dans un onglet spécifique
      const findDealInTab = async (tabIndex: number): Promise<boolean> => {
        try {
          // Changer vers l'onglet spécifique
          setSelectedTab(tabIndex);
          setPage(0); // Reset à la première page

          // Attendre que les données se chargent
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Récupérer le status de l'onglet
          let tabStatus;
          if (tabIndex === 0) tabStatus = undefined;
          else if (tabIndex === 1) tabStatus = 'pending';
          else if (tabIndex === 2) tabStatus = 'processing';
          else if (tabIndex === 3) tabStatus = 'completed';

          // Faire un appel API direct pour cet onglet
          const response = await makeAuthenticatedApiCall<{ daList: any[] }>('get', '/api/da', {
            params: {
              userPipedrive,
              userRole,
              page: 1,
              rowsPerPage: 1000, // Récupérer plus d'éléments pour la recherche
              status: tabStatus,
              sortColumn: 'R.RegistrationID',
              sortOrder: 'DESC',
              hidden: false,
              orgId: userRole === 'agency' ? orgIdValue : undefined,
            },
          });

          const tabData = response.data?.daList || [];
          const targetDeal = tabData.find((deal: any) => deal.RegistrationID === targetId);

          if (targetDeal) {
            console.log(`✅ Deal DA ${targetId} trouvé dans l'onglet ${tabIndex}`);
            return true;
          }

          return false;
        } catch (error) {
          console.error(`Erreur lors de la recherche dans l'onglet ${tabIndex}:`, error);
          return false;
        }
      };

      // Fonction pour ouvrir le drawer une fois l'onglet trouvé
      const openDrawer = () => {
        setTimeout(() => {
          const commentButton = document.querySelector(
            `[data-registration-id="${openExternalCommentsId}"] .external-comments-button`,
          );
          if (commentButton) {
            (commentButton as HTMLElement).click();
            console.log(`🎯 Drawer DA ouvert pour le deal ${openExternalCommentsId}`);

            // Nettoyer l'URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('openExternalComments');
            window.history.replaceState({}, '', newUrl.toString());
          } else {
            console.warn(`⚠️ Bouton commentaire DA non trouvé pour le deal ${openExternalCommentsId}`);
          }
        }, 1000);
      };

      // Rechercher dans l'onglet actuel d'abord
      const currentTabData = daListData || [];
      const targetDealInCurrentTab = currentTabData.find((deal: any) => deal.RegistrationID === targetId);

      if (targetDealInCurrentTab) {
        console.log(`✅ Deal DA ${targetId} trouvé dans l'onglet actuel`);
        openDrawer();
        return;
      }

      // Si pas trouvé dans l'onglet actuel, rechercher dans tous les onglets
      console.log(`🔍 Deal DA ${targetId} non trouvé dans l'onglet actuel, recherche dans tous les onglets...`);

      const searchInAllTabs = async () => {
        // Définir les onglets à vérifier
        const tabsToCheck = [0, 1, 2, 3]; // Tous les onglets DA

        for (const tabIndex of tabsToCheck) {
          if (tabIndex === selectedTab) continue; // Skip l'onglet actuel déjà vérifié

          console.log(`🔍 Recherche DA dans l'onglet ${tabIndex}...`);
          const found = await findDealInTab(tabIndex);

          if (found) {
            openDrawer();
            return;
          }
        }

        // Si pas trouvé dans aucun onglet
        console.warn(`❌ Deal DA ${targetId} non trouvé dans aucun onglet`);
        alert(
          `La DA avec l'ID ${targetId} n'a pas été trouvée. Il est possible qu'elle ait été supprimée ou déplacée.`,
        );

        // Nettoyer l'URL même si pas trouvé
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('openExternalComments');
        window.history.replaceState({}, '', newUrl.toString());
      };

      searchInAllTabs();
    }
  }, [openExternalCommentsId, daListData, isLoading, selectedTab, userPipedrive, userRole, orgIdValue]);

  // Gérer la recherche automatique par plaque d'immatriculation depuis les mails
  useEffect(() => {
    if (autoSearchRegPlate && !isLoading && !autoSearchDone.current) {
      autoSearchDone.current = true;
      console.log(`🔍 Recherche automatique DA déclenchée pour la plaque: ${autoSearchRegPlate}`);

      // Pré-remplir le champ de recherche
      handleSearch(autoSearchRegPlate, refetch);

      // Attendre que la recherche se termine et que les résultats soient affichés
      setTimeout(() => {
        // Vérifier qu'il y a des résultats de recherche
        if (daListData && daListData.length > 0) {
          console.log(
            `✅ Recherche DA terminée avec ${daListData.length} résultats pour la plaque: ${autoSearchRegPlate}`,
          );

          const firstResult = daListData[0];
          if (firstResult) {
            console.log(`🎯 Tentative d'ouverture du drawer DA pour le deal: ${firstResult.RegistrationID}`);

            setTimeout(() => {
              const commentButton = document.querySelector(
                `[data-registration-id="${firstResult.RegistrationID}"] .external-comments-button`,
              );
              if (commentButton) {
                (commentButton as HTMLElement).click();
                console.log(`🎯 Drawer DA ouvert avec succès pour la plaque: ${autoSearchRegPlate}`);
              } else {
                console.warn(`⚠️ Bouton commentaire DA non trouvé pour le deal: ${firstResult.RegistrationID}`);
              }
              setIsAutoSearchLoading(false);
            }, 1000);
          } else {
            setIsAutoSearchLoading(false);
          }
        } else {
          console.warn(`⚠️ Aucun résultat DA trouvé pour la plaque: ${autoSearchRegPlate}`);
          setIsAutoSearchLoading(false);
        }

        // Nettoyer l'URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('autoSearch');
        window.history.replaceState({}, '', newUrl.toString());

        console.log(`✅ Recherche automatique DA terminée pour la plaque: ${autoSearchRegPlate}`);
      }, 2000);
    }
  }, [autoSearchRegPlate, isLoading, daListData]);

  const handleDeleteDA = async (registrationId: number) => {
    const confirmDelete = window.confirm('Êtes-vous sûr de vouloir supprimer cette DA ?');
    if (confirmDelete) {
      setIsDeleting(true);
      try {
        await makeAuthenticatedApiCall('delete', `/api/da/${registrationId}`);

        // Invalider agressivement toutes les queries DA pour mettre à jour immédiatement tous les onglets
        console.log('🗑️ Invalidation complète du cache DA après suppression...');
        await queryClient.invalidateQueries({ queryKey: ['useDAList'] });
        await queryClient.refetchQueries({ queryKey: ['useDAList'] });

        alert('La DA a été supprimée avec succès.');
      } catch (error) {
        console.error('Erreur lors de la suppression de la DA:', error);
        alert('Une erreur est survenue lors de la suppression de la DA.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleRestoreDA = async () => {
    if (!restoreTargetId) return;
    setIsDeleting(true);
    setRestoreDialogOpen(false);
    try {
      await makeAuthenticatedApiCall('patch', `/api/da/${restoreTargetId}/visibility`, { visible: true });
      await queryClient.invalidateQueries({ queryKey: ['useDAList'] });
      await queryClient.refetchQueries({ queryKey: ['useDAList'] });
      alert('La DA a été restaurée avec succès.');
    } catch (error) {
      console.error('Erreur lors de la restauration de la DA:', error);
      alert('Une erreur est survenue lors de la restauration de la DA.');
    } finally {
      setIsDeleting(false);
      setRestoreTargetId(null);
    }
  };

  const handleOpenPaymentModal = (invoice: string, daFeeHt: number | string) => {
    setSelectedInvoice(invoice);
    setPrice(getDaFeeTtc(daFeeHt));
    setOpenPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  const handleChangePage = (_event: React.MouseEvent | null, newPage: number) => {
    setIsTableLoading(true);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsTableLoading(true);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column: string) => {
    const isAsc = sortColumn === column && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortColumn(column);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setIsTableLoading(true);
    setSelectedTab(newValue);
    setPage(0);
  };

  async function handleDAClick(registrationId: number) {
    navigate(`/form-da?registrationId=${registrationId}`);
  }

  const handleOpenRegenerateDialog = (da: any) => {
    setDealToRegenerate(da);
    setAgencyId(da.AgencyID); // Pré-remplir avec l'ID de l'agence de la DA
    setPersonId(da.person_id); // Pré-remplir avec l'ID de la personne si disponible
    setOpenRegenerateDialog(true);
  };

  const handleCloseRegenerateDialog = () => {
    setOpenRegenerateDialog(false);
  };

  const handleConfirmRegenerateInvoice = async () => {
    if (!dealToRegenerate || !agencyId) return;

    console.log('handleConfirmRegenerateInvoice / dealToRegenerate / ', JSON.stringify(dealToRegenerate, null, 2));
    console.log('handleConfirmRegenerateInvoice / agencyId / ', agencyId);
    console.log('handleConfirmRegenerateInvoice / personId / ', personId);

    setIsLoadingInvoice(true);
    try {
      // 🔍 Récupérer les données complètes de l'organisation depuis la base de données
      console.log(`🔍 Récupération des données de l'organisation ${agencyId}...`);
      const orgResponse = await makeAuthenticatedApiCall('get', `/api/pipedrive/organizations-in-db/${agencyId}`);
      const orgData = orgResponse.data as OrganizationData;

      console.log('📋 Données organisation récupérées:', orgData);

      // Aussi récupérer les données de la personne si disponible
      let personData: PersonData | null = null;
      if (personId) {
        try {
          console.log(`🔍 Récupération des données de la personne ${personId}...`);
          const personResponse = await makeAuthenticatedApiCall('get', `/api/pipedrive/persons-in-db/${personId}`);
          personData = personResponse.data as PersonData;
          console.log('👤 Données personne récupérées:', personData);
        } catch (personError) {
          console.warn('⚠️ Impossible de récupérer les données de la personne:', personError);
        }
      }

      // Fonction helper pour récupérer l'email avec tous les fallbacks possibles
      const getAgencyEmail = (): string | null => {
        // 1. Email de l'organisation (priorité absolue)
        if (orgData.email) return orgData.email;

        // 2. Email de la personne principale de l'organisation
        if (orgData.person_email) return orgData.person_email;

        // 3. Email principal de la personne (si récupérée)
        if (personData?.email) return personData.email;

        // 4. Email secondaire de la personne (si récupérée)
        if (personData?.secondary_email) return personData.secondary_email;

        // Aucun email trouvé - retourner null au lieu de générer un faux email
        return null;
      };

      const agencyEmail = getAgencyEmail();

      // Vérifier qu'on a un email avant de continuer
      if (!agencyEmail) {
        console.error('❌ Aucun email trouvé pour l\'agence', {
          agencyId,
          personId,
          orgData: {
            email: orgData.email,
            person_email: orgData.person_email,
          },
          personData: {
            email: personData?.email,
            secondary_email: personData?.secondary_email,
          },
        });
        alert(
          "Impossible de récupérer l'email de l'agence. Veuillez contacter l'administrateur à luc@aptekaa.fr",
        );
        setIsLoadingInvoice(false);
        return;
      }

      // Construire les données d'agence avec les vraies informations
      const agencyData = {
        personID: personId,
        agencyID: agencyId,
        agencyEmail,
        agencyName: orgData.name || dealToRegenerate.AgencyName || 'Agence',
        agencyAddress: orgData.address || 'Adresse agence',
        agencyCity: orgData.town || 'Ville',
        agencyPostalCode: orgData.post_code || '00000',
        agencySiret: orgData.siret || null, // Ajouter le SIRET de l'organisation
      };

      console.log('🏢 Données agence construites:', agencyData);

      await makeAuthenticatedApiCall('post', '/api/qonto/generate', {
        pipeline: 'da',
        deal: { id: dealToRegenerate.RegistrationID },
        vat: 0.2, // 20% TVA pour les DA
        sellingPrice: '10', // Prix fixe de 10€ HT pour les DA
        agencyData,
        regPlate: dealToRegenerate.regPlate,
        // Régénération : on passe outre la garde d'idempotence côté backend.
        // L'ancienne row est marquée annulée localement ; l'annulation côté Qonto reste manuelle.
        forceRegenerate: true,
      });

      // Rafraîchir la liste après régénération
      await refetch();

      console.log('✅ Facture DA régénérée avec succès');
    } catch (error: any) {
      console.error('❌ Erreur lors de la régénération de la facture DA:', error);
      const message = error?.response?.data?.message;
      if (message) {
        alert(message);
      }
    }
    setIsLoadingInvoice(false);
    handleCloseRegenerateDialog();
  };

  const onRefresh = async (forceFullSync = false) => {
    if (forceFullSync) {
      setIsFullSyncLoading(true);
    }
    setIsTableLoading(true);

    try {
      // Synchroniser les statuts des factures Qonto (déclenche automatiquement les transitions de statuts DA)
      try {
        console.log(`🔄 Début de la synchronisation des factures Qonto DA${forceFullSync ? ' (COMPLÈTE)' : ''}...`);
        await makeAuthenticatedApiCall('post', '/api/qonto/sync/invoices/da');
        console.log('✅ Synchronisation des factures Qonto DA effectuée (transitions automatiques)');

        // Attendre un délai adaptatif selon le type de sync
        const delay = forceFullSync ? 2000 : 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
        console.log(`⏳ Délai de propagation terminé (${delay}ms)`);
      } catch (err) {
        console.error('Erreur lors de la synchronisation des statuts de factures Qonto DA', err);
      }

      // Invalider le cache de manière plus agressive
      console.log('🗑️ Invalidation du cache React Query...');

      if (forceFullSync) {
        // Synchronisation complète : invalider tout le cache
        console.log('🔄 Synchronisation complète : invalidation de tout le cache...');
        await queryClient.invalidateQueries();
        await queryClient.refetchQueries();
      } else {
        // Synchronisation normale : invalider seulement les DA
        await queryClient.invalidateQueries({ queryKey: ['da-list'] });
      }

      // Forcer le refetch des données actuelles
      await refetch();

      // Attendre encore un peu pour s'assurer que les nouvelles données sont chargées
      const finalDelay = forceFullSync ? 1000 : 500;
      await new Promise((resolve) => setTimeout(resolve, finalDelay));

      console.log(`🔄 Actualisation des données DA terminée${forceFullSync ? ' (COMPLÈTE)' : ''}`);
    } catch (error) {
      console.error("Erreur lors de l'actualisation:", error);
    } finally {
      setIsTableLoading(false);
      if (forceFullSync) {
        setIsFullSyncLoading(false);
      }
    }
  };

  if (error) return <Typography color="error">Erreur: {error.message}</Typography>;

  // N'afficher le message "Pas encore de déclaration d'achat" que s'il n'y a vraiment AUCUNE DA
  if (!isLoading && !isTableLoading && !searchQuery && dataTotalCheck?.total === 0) {
    return (
      <Container maxWidth={false}>
        <Grid item xs={12} sx={{ marginBottom: 5 }}>
          <Typography align="center" mt={5} variant="body2">
            📅 Vous désirez passer sur une facturation à la quinzaine ou mensuelle :{' '}
            <Link target="_blank" href="mailto:skipcarcg@gmail.com">
              Contactez-nous
            </Link>{' '}
            ✉️
          </Typography>
        </Grid>

        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '200px' }}>
          <Paper
            elevation={5}
            sx={{
              padding: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              marginTop: theme.spacing(0),
              width: '100%',
              maxWidth: '500px',
              background: 'linear-gradient(135deg, #fff8e1 0%, #ffe57f 100%)',
            }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{
                marginBottom: theme.spacing(1),
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              📊 Pas encore de déclaration d'achat
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{
                marginBottom: theme.spacing(2),
                color: 'black',
              }}
            >
              Les déclarations d'achat sont uniquement à 10€ HT. Profitez en !
            </Typography>
            <Button
              variant="contained"
              color="primary"
              type="button"
              className="ActionButton"
              sx={{
                width: '350px',
                margin: '22px auto !important',
                backgroundColor: theme.palette.saffron.main,
                '&:hover': {
                  backgroundColor: theme.palette.saffron.dark,
                },
              }}
              href="/form-da"
            >
              🚗 Faire une déclaration d'achat
            </Button>
          </Paper>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}
        open={isAutoSearchLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" color="inherit">
          Chargement en cours...
        </Typography>
      </Backdrop>
      <Grid item xs={12}>
        <Typography align="center" mt={5} variant="body2">
          📅 Vous désirez passer sur une facturation à la quinzaine ou mensuelle :{' '}
          <Link target="_blank" href="mailto:skipcarcg@gmail.com">
            Contactez-nous
          </Link>{' '}
          ✉️
        </Typography>
      </Grid>

      {!currentUser?.data?.organization?.value && (
        <Typography variant="h6" sx={{ mt: 4, textAlign: 'center' }}>
          Il semblerait y avoir un problème dans la configuration de votre compte, veuillez nous contacter au 07 80 94
          97 37.
        </Typography>
      )}

      {currentUser?.data?.organization?.value && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                label="Rechercher"
                className="inputElement"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value, refetch)}
                sx={{ marginRight: 0 }}
              />
              <Button
                variant="contained"
                onClick={() => handleSearch(searchQuery, refetch)}
                className="dealActionButton"
                sx={{ ml: 1, fontWeight: 900 }}
              >
                Rechercher
              </Button>
              {hasSearched && (
                <Button
                  className="secondaryDealActionButton"
                  variant="contained"
                  sx={{ ml: 1 }}
                  color="error"
                  onClick={() => resetSearch(refetch)}
                >
                  Revenir à la liste principale
                </Button>
              )}
            </Box>
            <TablePagination
              className="custom-pagination"
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={totalItems || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={'Lignes par page'}
              labelDisplayedRows={({ from, to }) => `de ${from} à ${to}`}
            />
          </Box>

          {userRole !== 'agency' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  className="dealActionButton"
                  sx={{ fontWeight: 800 }}
                  onClick={() => onRefresh()}
                  disabled={isTableLoading || isFullSyncLoading}
                >
                  {isTableLoading && !isFullSyncLoading ? 'Rafraîchissement...' : 'Rafraichir / Mettre à jour'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  className="secondaryDealActionButton"
                  sx={{ fontWeight: 600 }}
                  onClick={() => onRefresh(true)}
                  disabled={isTableLoading || isFullSyncLoading}
                  title="Synchronisation complète avec délais plus longs et invalidation totale du cache"
                >
                  {isFullSyncLoading ? 'Sync complète...' : 'Sync complète'}
                </Button>
              </Box>
            </Box>
          )}

          <Paper sx={{ marginTop: 5, padding: 0 }} elevation={3}>
            {userRole !== 'agency' && !hasSearched && (
              <Tabs value={selectedTab} className="DealsListTabs" onChange={handleTabChange} aria-label="DA List Tabs">
                <Tab label="Toutes" />
                <Tab label="En attente de paiement" />
                <Tab label="En attente de traitement" />
                <Tab label="Terminé" />
                {userRole !== 'agency' && <Tab label={<span style={{ color: 'red' }}>DA supprimées</span>} />}
              </Tabs>
            )}
            <Box
              sx={{
                textAlign: 'center',
                fontSize: 20,
                fontWeight: 600,
                background: '#979ED2',
                width: '100%',
                fontFamily: 'Urbanist',
                paddingTop: 2,
                paddingBottom: 2,
                color: 'white',
              }}
            >
              Liste des déclarations d'achat
            </Box>
            <Table stickyHeader>
              <TableHead className="stickyTableHead">
                <TableRow sx={{ background: '#262626' }}>
                  {userRole !== 'agency' && (
                    <TableCell sx={{ ...stickyCellStyle, width: '30px', padding: 0, paddingLeft: 1 }}></TableCell>
                  )}
                  {userRole !== 'agency' && (
                    <TableCell
                      sx={{ ...stickyCellStyle, width: '50px', cursor: 'pointer' }}
                      onClick={() => handleSort('R.id')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        ID
                        {sortColumn === 'R.id' && (
                          <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ ...stickyCellStyle, width: '90px', cursor: 'pointer' }}
                    onClick={() => handleSort('R.regPlate')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Immat
                      {sortColumn === 'R.regPlate' && (
                        <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...stickyCellStyle }}>Vendeur / Acheteur</TableCell>
                  <TableCell sx={{ ...stickyCellStyle, width: '100px' }}>Prix</TableCell>
                  {userRole !== 'agency' && <TableCell sx={{ ...stickyCellStyle, width: '75px' }}>Agence</TableCell>}
                  <TableCell
                    sx={{ ...stickyCellStyle, cursor: 'pointer' }}
                    onClick={() => handleSort('R.purchaseDateTime')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date de vente
                      {sortColumn === 'R.purchaseDateTime' && (
                        <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...stickyCellStyle, cursor: 'pointer' }} onClick={() => handleSort('R.createdAt')}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date de création
                      {sortColumn === 'R.createdAt' && (
                        <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...stickyCellStyle, cursor: 'pointer' }} onClick={() => handleSort('R.updatedAt')}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Dernière modification
                      {sortColumn === 'R.updatedAt' && (
                        <SortIcon>{sortOrder === 'ASC' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}</SortIcon>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...stickyCellStyle }}>Commentaires</TableCell>
                  <TableCell sx={{ ...stickyCellStyle }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isTableLoading ? (
                  <TableRow>
                    <TableCell colSpan={userRole === 'agency' ? 7 : 9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  daListData.map((da: DAItem) => (
                    <TableRow key={da.RegistrationID}>
                      {userRole !== 'agency' && (
                        <TableCell sx={{ width: '30px', padding: 0, paddingLeft: 1, borderBottom: '2px solid' }}>
                          {isDeleting ? (
                            <CircularProgress size={24} />
                          ) : selectedTab === DELETED_TAB_INDEX ? (
                            <RestoreFromTrashIcon
                              sx={{ cursor: 'pointer', color: 'green' }}
                              onClick={() => {
                                setRestoreTargetId(da.RegistrationID);
                                setRestoreDialogOpen(true);
                              }}
                            />
                          ) : (
                            <DeleteIcon
                              sx={{ cursor: 'pointer', color: 'red' }}
                              onClick={() => handleDeleteDA(da.RegistrationID)}
                            />
                          )}
                        </TableCell>
                      )}
                      {userRole !== 'agency' && (
                        <TableCell sx={{ borderBottom: '2px solid' }}>{da.RegistrationID}</TableCell>
                      )}
                      <TableCell sx={{ minWidth: '90px', borderBottom: '2px solid' }}>{da.regPlate}</TableCell>
                      <TableCell sx={{ borderBottom: '2px solid' }}>
                        {da.SellerClientType === 'pro' && da.SellerCompanyName
                          ? da.SellerCompanyName
                          : `${da.SellerFirstName || ''} ${da.SellerLastName || ''}`.trim() || 'Non renseigné'}
                        <Divider sx={{ marginTop: 3, marginBottom: 1 }} />
                        {da.BuyerClientType === 'pro' && da.BuyerCompanyName
                          ? da.BuyerCompanyName
                          : `${da.BuyerFirstName || ''} ${da.BuyerLastName || ''}`.trim() || 'Non renseigné'}
                      </TableCell>
                      <TableCell style={{ fontWeight: 600, borderBottom: '2px solid' }}>{formatDaFeeTtc(da.daFee)}</TableCell>
                      {userRole !== 'agency' && (
                        <TableCell sx={{ borderBottom: '2px solid' }}>{da.AgencyName}</TableCell>
                      )}
                      <TableCell sx={{ borderBottom: '2px solid' }}>
                        {formatDateToDDMMYYYY(da.purchaseDateTime)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '2px solid' }}>{formatDateToDDMMYYYY(da.createdAt)}</TableCell>
                      <TableCell sx={{ borderBottom: '2px solid' }}>{formatDateToDDMMYYYY(da.updatedAt)}</TableCell>
                      <TableCell sx={{ minWidth: '200px', borderBottom: '2px solid' }}>
                        <CommentsManager
                          registrationId={da.RegistrationID}
                          internalComments={da.comments || []}
                          userRole={userRole}
                          type="da"
                          refetch={async () => {
                            await refetch();
                          }}
                          maxWidth={200}
                          regPlate={da.regPlate}
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: '2px solid' }}>
                        <Stack direction="column" spacing={1} alignItems="center">
                          {da.status === 'DA_REQUEST' && (
                            <Button
                              sx={{
                                fontSize: 14,
                                background: '#FF6060',
                                opacity: 1,
                                fontWeight: 600,
                                width: 152,
                              }}
                              className="dealActionButton"
                              onClick={() => handleOpenPaymentModal(da.invoice_number || '', da.daFee)}
                              variant="contained"
                            >
                              Payer
                            </Button>
                          )}
                          {userRole === 'agency' && da.status === 'DA_REQUEST' && (
                            <>
                              <Typography variant="body2" sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}>
                                Référence pour le virement : {da.invoice_number}
                              </Typography>
                              <Typography variant="body2" sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}>
                                Prix à payer : {formatDaFeeTtc(da.daFee)}
                              </Typography>
                            </>
                          )}
                          <Button
                            sx={{
                              fontSize: 14,
                              width: 152,
                              display: da.invoice_url_firebase ? 'block' : 'none',
                            }}
                            className="secondaryDealActionButton"
                            variant="contained"
                            onClick={() => window.open(da.invoice_url_firebase, '_blank')}
                          >
                            Voir la facture
                          </Button>

                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {da.invoice_number} {da.invoice_status && `/ ${t(`invoiceStatus.${da.invoice_status}`)}`}
                          </Typography>

                          <Button
                            sx={{ fontSize: 14, width: 152 }}
                            className="secondaryDealActionButton"
                            variant="contained"
                            onClick={() => handleDAClick(da.RegistrationID)}
                          >
                            Voir la demande
                          </Button>

                          <SubmitFormDA registrationId={da.RegistrationID} userRole={userRole} onRefresh={refetch} />

                          {userRole === 'god' && currentUser?.authUser?.uid === restrictedUserId && (
                            <Button
                              sx={{
                                fontSize: 14,
                                width: 152,
                              }}
                              className="dealActionButton"
                              variant="contained"
                              onClick={() => handleOpenRegenerateDialog(da)}
                              disabled={isLoadingInvoice}
                            >
                              {isLoadingInvoice ? <CircularProgress size={24} /> : 'Régénérer la facture'}
                            </Button>
                          )}

                          <Typography
                            align="center"
                            sx={{
                              fontSize: '16px',
                              lineHeight: '1.25em',
                              ...getStatusStyle(da.status),
                            }}
                            variant="body2"
                          >
                            {getStatusLabel(da.status)}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: 2,
              }}
            >
              <TablePagination
                className="custom-pagination"
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={'Lignes par page'}
                labelDisplayedRows={({ from, to }) => `de ${from} à ${to}`}
              />
            </Box>
          </Paper>
        </>
      )}
      <PaymentModal
        open={openPaymentModal}
        onClose={handleClosePaymentModal}
        price={price}
        invoiceNumber={selectedInvoice}
      />
      <Dialog open={openRegenerateDialog} onClose={handleCloseRegenerateDialog}>
        <DialogTitle>Régénérer la facture DA</DialogTitle>
        <DialogContent>
          <TextField
            label="Person ID"
            type="number"
            value={personId}
            onChange={(e) => setPersonId(Number(e.target.value))}
            fullWidth
            margin="dense"
            helperText="L'ID de la personne (contact) pour laquelle régénérer la facture"
          />
          <TextField
            label="Agency ID"
            type="number"
            value={agencyId}
            onChange={(e) => setAgencyId(Number(e.target.value))}
            fullWidth
            margin="dense"
            helperText="L'ID de l'organisation (agence) pour laquelle régénérer la facture"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegenerateDialog} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmRegenerateInvoice} color="primary" disabled={isLoadingInvoice}>
            {isLoadingInvoice ? <CircularProgress size={20} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Restaurer la DA</DialogTitle>
        <DialogContent>
          <Typography>Êtes-vous sûr de vouloir restaurer cette DA ? Elle sera de nouveau visible dans la liste active.</Typography>
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={() => setRestoreDialogOpen(false)} color="primary">
            Annuler
          </Button>
          <Button className="dealActionButton" onClick={handleRestoreDA} color="primary">
            Restaurer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DAList;
